import * as React from 'react';
import { ProductSizeInterface } from '../../../Interface/ProductSize';
import { Col, Row } from 'react-bootstrap';
import { BrandInterface } from '../../../Interface/Brand';
import ProductSizeDetails from './ProductSizeDetails/ProductSizeDetails';
import ProductSizeImage from './ProductSizeImage/ProductSizeImage';
import ProductAttributes from './ProductAttributes/ProductAttributes';
import ProductSizeSellSheet from './ProductSizeSellSheet/ProductSizeSellSheet';
import { useAtom } from 'jotai';
import { selectedFlavorFamily } from '../../Atoms/ProductAtoms';
import ProductSizeGallery from './ProductSizeGallery/ProductSizeGallery';
import Button from '../Button/Button';
import LoginButtonSpan from '../LoginButtonSpan/LoginButtonSpan';
import Axios from 'axios';
import { useUser } from '../../Context/UserContext';
import { useProductList } from '../../Context/ProductListContext';
import styles from './ProductSize.module.scss?module';
import { formatCurrencyEur } from '../../Helper/formatter';
import clsx from 'clsx';

export default function ProductSize({
  brand,
  product_size,
  onUpdate,
  allowBackorder = false,
}: {
  brand?: BrandInterface;
  product_size: ProductSizeInterface;
  onUpdate?: () => void;
  allowBackorder?: boolean;
}) {
  const { user } = useUser();
  const {
    addToBasket,
    isPending,
    isSuccess,
    flavoursToAdd,
    getUnits,
    getTotal,
  } = useProductList();

  const [selectedFlavor, setSelectedFlavor] = useAtom(
    selectedFlavorFamily(product_size.id ?? 0)
  );
  const [showImageGalerie, setShowImageGalerie] = React.useState(false);

  const openGallery = () => {
    if (!selectedFlavor?.id || !selectedFlavor?.flavorImages) return;
    setShowImageGalerie(true);
    Axios.get(
      `/api/onlineshop/product-attributes/${selectedFlavor.id}/product-images`
    )
      .then((resp) => {
        setSelectedFlavor({ id: selectedFlavor.id, flavorImages: resp.data });
      })
      .catch(() => {});
  };

  React.useEffect(() => {
    if (product_size.default_selected_product_attribute_id) {
      setSelectedFlavor({
        id: product_size.default_selected_product_attribute_id,
        flavorImages: product_size.default_selected_product_attribute_images,
      });
    }
  }, [product_size]);

  if (!product_size) return null;
  return (
    <li className="list-group-item">
      <Row className="mb-3">
        <ProductSizeDetails
          brand={brand}
          product_size={product_size}
          onUpdate={onUpdate}
        />
        <ProductSizeImage
          product_size={product_size}
          onImageClick={openGallery}
        />
      </Row>
      <Row>
        <ProductSizeSellSheet product_size={product_size} />
      </Row>
      <Row className="g-0">
        <ProductAttributes
          product_size={product_size}
          allowBackorder={allowBackorder}
        />
      </Row>
      <Row className="g-0 mt-4">
        <Col xs={12} sm={8} md={9} xxl={10}>
          <div className="w-100 text-end fw-bold h-100 align-items-center d-flex justify-content-end pe-md-4">
            {user &&
            flavoursToAdd.filter(({ flavourId }) =>
              product_size.product_attributes?.some(
                ({ erp_id }) => erp_id === flavourId
              )
            ).length > 0 ? (
              <>
                <span className="me-md-4 me-2">
                  UNITS:{' '}
                  {getUnits(
                    product_size.product_attributes.map(({ erp_id }) => erp_id)
                  )}
                </span>
                <span className="ms-md-4">
                  TOTAL:{' '}
                  {formatCurrencyEur(
                    getTotal(
                      product_size.product_attributes.map(
                        ({ erp_id }) => erp_id
                      )
                    )
                  )}
                </span>
              </>
            ) : (
              <></>
            )}
          </div>
        </Col>
        <Col sm={4} md={3} xxl={2} className="p-0">
          <div className="w-100 text-end">
            {user ? (
              <Button
                disabled={
                  isPending ||
                  getUnits(
                    product_size.product_attributes.map(({ erp_id }) => erp_id)
                  ) === 0
                }
                onClick={addToBasket}
                loading={isPending}
                success={isSuccess}
                className={clsx(styles.AddToBasketBtn)}
              >
                ADD TO BASKET
              </Button>
            ) : (
              <LoginButtonSpan />
            )}
          </div>
        </Col>
      </Row>
      {selectedFlavor.flavorImages && (
        <ProductSizeGallery
          showImageGalerie={showImageGalerie}
          setShowImageGalerie={setShowImageGalerie}
          images={selectedFlavor.flavorImages}
        />
      )}
    </li>
  );
}
